import { selectDevicesFields } from 'entities/device/lib';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { createSubsetSelector } from 'webui/utils/selectors';
import { get_z3k_object_state, WiredTemperatureSensor, } from 'z3k-config';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { getZ3KHeatingCircuitThermometerConfig, getZ3KHeatingCircuitThermometerState, } from 'console/shared/content/device/heating-circuits/z3k/utils';
import { useSelectDevice } from '../index';
import { get_all_temperature_sensors_plus_adapters, } from 'z3k-parser/lib/z3k-selectors';
import { z3k_key_namespace } from 'z3k-settings/utils/namespaces';
import { removeNulls } from 'utils';
import { Z3KSettings, Z3KSettingsEditor } from 'z3k-settings/provider/types';
export const useZ3KDashboardDevices = (ids) => {
    const devices = useAppSelector(selectDevicesFields([
        'id',
        'iconState',
        'name',
        'individual_device_name',
        'device_type',
    ]));
    // const z3k_devices = devices.filter(({device_type}) => !isLegacyDevice(device_type));
    return ids ? devices.filter((device) => ids.includes(device.id)) : devices;
};
export const useZ3KDeviceState = () => {
    const device = useSelectDevice(createSubsetSelector(['z3k_state']));
    const z3k_state = useMemo(() => {
        if (device) {
            return device.z3k_state;
        }
        return {};
    }, [device?.z3k_state]);
    return z3k_state;
};
export const useZ3KObjectState = (id, default_state) => {
    const z3k_state = useZ3KDeviceState();
    const z3k_object_state = useMemo(() => {
        const state = id ? get_z3k_object_state(z3k_state, id) : null;
        if (state) {
            return state;
        }
        return default_state ?? {};
    }, [z3k_state, id]);
    return z3k_object_state;
};
export const useHeatingCircuitTargetThermometer = (z3k_config, id, is_dhw = false) => {
    const z3k_state = useZ3KDeviceState();
    const config = useMemo(() => getZ3KHeatingCircuitThermometerConfig(z3k_config, id), [z3k_config, id]);
    const state = useMemo(() => getZ3KHeatingCircuitThermometerState(config, z3k_state, is_dhw), [config, z3k_state, is_dhw]);
    return { ...config, ...state };
};
export const useZ3KBoilerAdapterState = (id) => {
    const z3k_state = useZ3KDeviceState();
    const z3k_boiler_adapter_state = useMemo(() => {
        const state = id ? get_z3k_object_state(z3k_state, id) : null;
        if (state) {
            return state;
        }
        return { status: {}, mode: {}, flags: {}, ot: {} };
    }, [z3k_state, id]);
    return z3k_boiler_adapter_state;
};
export const useWeatherFlagSensor = () => {
    const { replace_config, config } = useContext(Z3KSettingsEditor);
    const { deviceFirmwareVersion } = useContext(Z3KSettings);
    const all_options_outside = useMemo(() => get_all_temperature_sensors_plus_adapters(config), [config]);
    const self = useCallback((group) => z3k_key_namespace[group], []);
    const getStreetSensor = useMemo(() => removeNulls(all_options_outside.map((el) => {
        const getOutside = (e, self) => e.find((i) => self.isOutside(i));
        if (self(el.group)) {
            return getOutside(el.options, self(el.group));
        }
        return null;
    })), [all_options_outside]);
    const getReserveStreetSensor = useMemo(() => removeNulls(all_options_outside.map((el) => {
        const getOutsideReserve = (e, self) => e.find((i) => self.isOutsideReserve(i));
        if (self(el.group)) {
            return getOutsideReserve(el.options, self(el.group));
        }
        return null;
    })), [all_options_outside]);
    const filter_options_for_outside_reserve = useMemo(() => getStreetSensor[0]?.id
        ? all_options_outside.map((group) => ({
            ...group,
            options: removeNulls(group.options.map((sensor) => sensor.id !== getStreetSensor[0]?.id ? sensor : null)),
        }))
        : all_options_outside, [all_options_outside, getStreetSensor]);
    const filter_options_for_outside = useMemo(() => getReserveStreetSensor[0]?.id
        ? all_options_outside.map((group) => ({
            ...group,
            options: removeNulls(group.options.map((sensor) => sensor.id !== getReserveStreetSensor[0]?.id
                ? sensor
                : null)),
        }))
        : all_options_outside, [all_options_outside, getReserveStreetSensor]);
    const onChangeStreetSensor = useCallback((value) => {
        const new_value = (exist_register, new_flag, checked) => {
            return (exist_register & ~new_flag) | (checked ? new_flag : 0);
        };
        let newConfig = { ...config };
        all_options_outside.forEach((groups) => {
            groups.options.forEach((sensor) => {
                const exist_register = sensor.setting_register ??
                    sensor.logged_parameter_register;
                const new_flag = z3k_key_namespace[groups.group].Flags.IS_OUTSIDE;
                if (sensor.id === value && sensor.id !== getStreetSensor[0]?.id) {
                    newConfig = z3k_key_namespace[groups.group].get.applySetterById(newConfig, sensor.id, {
                        [groups.field]: new_value(exist_register, new_flag, true),
                    });
                }
                if (sensor.id !== value && sensor.id === getStreetSensor[0]?.id) {
                    newConfig = z3k_key_namespace[groups.group].get.applySetterById(newConfig, sensor.id, {
                        [groups.field]: new_value(exist_register, new_flag, false),
                    });
                }
            });
        });
        return replace_config(newConfig);
    }, [config, all_options_outside, getStreetSensor]);
    const onChangeStreetSensorReserve = useCallback((value) => {
        const new_value = (exist_register, new_flag, checked) => {
            return (exist_register & ~new_flag) | (checked ? new_flag : 0);
        };
        let newConfig = { ...config };
        all_options_outside.forEach((groups) => {
            groups.options.forEach((sensor) => {
                const exist_register = sensor.setting_register ??
                    sensor.logged_parameter_register;
                const new_flag = z3k_key_namespace[groups.group].Flags.IS_OUTSIDE_RESERVE;
                if (sensor.id === value &&
                    sensor.id !== getReserveStreetSensor[0]?.id) {
                    newConfig = z3k_key_namespace[groups.group].get.applySetterById(newConfig, sensor.id, {
                        [groups.field]: new_value(exist_register, new_flag, true),
                    });
                }
                if (sensor.id !== value &&
                    sensor.id === getReserveStreetSensor[0]?.id) {
                    newConfig = z3k_key_namespace[groups.group].get.applySetterById(newConfig, sensor.id, {
                        [groups.field]: new_value(exist_register, new_flag, false),
                    });
                }
            });
        });
        return replace_config(newConfig);
    }, [config, all_options_outside, getReserveStreetSensor]);
    useEffect(() => {
        if (deviceFirmwareVersion >= 429) {
            const default_reserve_sensor = WiredTemperatureSensor.allInternetSensor(config)[0];
            const id = getStreetSensor[0]?.id;
            if (!getReserveStreetSensor[0] &&
                default_reserve_sensor &&
                id &&
                id != default_reserve_sensor.id) {
                onChangeStreetSensorReserve(default_reserve_sensor.id);
            }
        }
    }, [getReserveStreetSensor, getStreetSensor, deviceFirmwareVersion]);
    return {
        getStreetSensor,
        getReserveStreetSensor,
        filter_options_for_outside_reserve,
        filter_options_for_outside,
        onChangeStreetSensor,
        onChangeStreetSensorReserve,
    };
};
